import { useEffect, useRef, useState } from "react";
import type {
  GuestBranding,
  GuestContent,
  GuestResolveResponse,
} from "@shared/types/guest";
import { fetchContent, resolveToken, GuestApiError } from "./api";
import { startAnalytics, track, flush } from "./analytics";
import {
  Hero,
  WelcomeCard,
  PromoCard,
  SectionCard,
  WifiCard,
  AppsCard,
  LiveTvCard,
  RadioCard,
  InstagramCard,
  WeatherCard,
  LanguageSwitcher,
  OpenTargetProvider,
  useBranding,
  makeLocalize,
} from "./components";
import { OffersSection } from "./offers";
import { makeT, type Translate } from "@shared/guest-strings";

type Phase = "loading" | "ready" | "error";

const CACHE_KEY = "guest.content";
const LOCALE_KEY = "guest.locale";

/** Error codes that are a definitive "this session is no longer valid" - they
 *  must override any cached content (e.g. the room was checked out). */
const HARD_ERRORS = new Set(["checked_out", "invalid_token", "not_available"]);

function clearCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/** Pull the premise branding a checked-out response carries, so the error
 *  screen can render the full hotel page chrome. */
function brandingFromError(err: unknown): GuestBranding | null {
  if (
    err instanceof GuestApiError &&
    err.body &&
    typeof err.body === "object"
  ) {
    return (err.body as { branding?: GuestBranding }).branding ?? null;
  }
  return null;
}

function readCache(): GuestResolveResponse | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as GuestResolveResponse) : null;
  } catch {
    return null;
  }
}
function writeCache(d: GuestResolveResponse): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(d));
  } catch {
    /* quota / private mode - non-fatal */
  }
}

/** Best-effort locale before content resolves (loading + error screens):
 *  the guest's last saved choice, else English. */
function initialLocale(): string {
  try {
    const saved = localStorage.getItem(LOCALE_KEY);
    if (saved) return saved;
  } catch {
    /* ignore */
  }
  return "en";
}

/**
 * The offer a deep link asked for (`POS-13`), read once at boot.
 *
 * `?offer=` is the one companion URL definition's only scope parameter -
 * `guestTokenUrl()` on the server builds it, whether the guest arrived from a
 * printed code or came back from somewhere. It is a HINT and never a
 * credential: the room comes from the token or the cookie, so a hand-typed
 * offer id can only ever scroll a page.
 *
 * Read before `history.replaceState` wipes the URL, which is why it is called
 * from a `useState` LAZY INITIALISER rather than from the effect that resolves
 * the token: initialisers run during the first render, effects after it.
 */
function initialOfferId(): string | null {
  try {
    const raw = new URLSearchParams(window.location.search).get("offer");
    const id = (raw ?? "").trim();
    return id ? id.slice(0, 64) : null;
  } catch {
    return null;
  }
}

function pickLocale(d: GuestResolveResponse): string {
  const supported = d.content.supportedLocales ?? [];
  try {
    const saved = localStorage.getItem(LOCALE_KEY);
    if (saved && supported.includes(saved)) return saved;
  } catch {
    /* ignore */
  }
  return d.content.defaultLocale || supported[0] || "en";
}

export default function App() {
  const [phase, setPhase] = useState<Phase>("loading");
  const [data, setData] = useState<GuestResolveResponse | null>(null);
  const [locale, setLocale] = useState("en");
  const [errorCode, setErrorCode] = useState<string | undefined>();
  // Premise branding carried on a checked-out response, so the error screen can
  // wear the full hotel page (hero image + theme) instead of a bare card.
  const [errorBranding, setErrorBranding] = useState<GuestBranding | null>(
    null
  );
  const bootstrapped = useRef(false);
  // Captured before the token is stripped from the URL below.
  const [focusOfferId] = useState(initialOfferId);

  useBranding(data?.content ?? null);

  useEffect(() => {
    if (bootstrapped.current) return; // guard StrictMode double-invoke
    bootstrapped.current = true;

    const accept = (d: GuestResolveResponse) => {
      setData(d);
      setLocale(pickLocale(d));
      writeCache(d);
      setPhase("ready");
      startAnalytics();
      track("view");
    };

    const match = window.location.pathname.match(/^\/r\/(.+)$/);
    if (match) {
      const token = decodeURIComponent(match[1]);
      resolveToken(token)
        .then((d) => {
          // Drop the token from the URL/history - it's a one-time bearer now
          // exchanged for the session cookie.
          window.history.replaceState({}, "", "/");
          accept(d);
        })
        .catch((err: unknown) => {
          const code = err instanceof GuestApiError ? err.code : "network";
          // A definitive "not valid / checked out" verdict beats any stale cache.
          if (code && HARD_ERRORS.has(code)) {
            clearCache();
            setData(null);
            setErrorBranding(brandingFromError(err));
            setErrorCode(code);
            setPhase("error");
            return;
          }
          const cached = readCache();
          if (cached) return accept(cached);
          setErrorCode(code);
          setPhase("error");
        });
      return;
    }

    // Re-open (no token in URL): show cache instantly, refresh in the background.
    const cached = readCache();
    if (cached) accept(cached);
    fetchContent()
      .then(accept)
      .catch((err: unknown) => {
        const code = err instanceof GuestApiError ? err.code : "no_session";
        // Checked-out / invalidated sessions must clear the cached view too.
        if (code && HARD_ERRORS.has(code)) {
          clearCache();
          setData(null);
          setErrorBranding(brandingFromError(err));
          setErrorCode(code);
          setPhase("error");
          return;
        }
        if (cached) return; // keep showing cache on a transient error
        setErrorCode(code);
        setPhase("error");
      });
  }, []);

  if (phase === "loading") {
    return (
      <div className="state">
        <div
          className="spinner"
          aria-label={makeT(initialLocale())("loading")}
        />
      </div>
    );
  }

  if (phase === "error" || !data) {
    return <ErrorState code={errorCode} branding={errorBranding} />;
  }

  const { content, roomLabel } = data;
  const L = makeLocalize(locale, content.defaultLocale);
  const t = makeT(locale);

  const onLocale = (loc: string) => {
    setLocale(loc);
    try {
      localStorage.setItem(LOCALE_KEY, loc);
    } catch {
      /* ignore */
    }
    track("language_switch", loc);
  };

  return (
    <OpenTargetProvider canOpenOnTv={!!data.canOpenOnTv} t={t}>
      <div className="app">
        <Hero content={content} roomLabel={roomLabel} L={L} />
        <main className="stack">
          {content.tvApp && <LiveTvCard app={content.tvApp} t={t} />}
          {content.welcome && (
            <WelcomeCard
              title={L(content.welcome.title)}
              body={L(content.welcome.body)}
            />
          )}
          {content.apps.length > 0 && <AppsCard apps={content.apps} t={t} />}
          {content.stations.length > 0 && (
            <RadioCard stations={content.stations} L={L} t={t} />
          )}
          {content.instagram && (
            <InstagramCard
              instagram={content.instagram}
              premiseId={data.premiseId}
              L={L}
              t={t}
            />
          )}
          {content.weather && (
            <WeatherCard weather={content.weather} locale={locale} t={t} />
          )}
          {content.promos.map((p) => (
            <PromoCard key={p.id} promo={p} L={L} />
          ))}
          <OffersSection
            offers={content.offers ?? []}
            canOrder={!!data.canOrder}
            focusOfferId={focusOfferId}
            L={L}
            t={t}
            locale={locale}
          />
          {content.wifi && <WifiCard wifi={content.wifi} L={L} t={t} />}
          {content.sections.map((s) => (
            <SectionCard key={s.id} section={s} L={L} />
          ))}
          {content.relatives.length > 0 && (
            <>
              <h2 className="group">{t("for_family")}</h2>
              {content.relatives.map((s) => (
                <SectionCard key={s.id} section={s} L={L} />
              ))}
            </>
          )}
        </main>
        <footer className="footer">
          <LanguageSwitcher
            locales={content.supportedLocales ?? []}
            value={locale}
            onChange={onLocale}
          />
          <PrivacyNote t={t} />
        </footer>
      </div>
    </OpenTargetProvider>
  );
}

function PrivacyNote(props: { t: Translate }) {
  const { t } = props;
  const [open, setOpen] = useState(false);
  return (
    <div className="privacy">
      <button
        className="privacy__toggle"
        onClick={() => {
          setOpen((o) => !o);
          flush();
        }}
      >
        {t("privacy")}
      </button>
      {open && <p className="privacy__text">{t("privacy_text")}</p>}
    </div>
  );
}

function ErrorState(props: { code?: string; branding?: GuestBranding | null }) {
  const t = makeT(initialLocale());
  const checkedOut = props.code === "checked_out";
  const invalid =
    props.code === "invalid_token" || props.code === "not_available";
  const title = checkedOut
    ? t("err_checked_out_title")
    : invalid
      ? t("err_invalid_title")
      : t("err_none_title");
  const body = checkedOut
    ? t("err_checked_out_body")
    : invalid
      ? t("err_invalid_body")
      : t("err_none_body");

  // When we have the premise branding (checked-out flow), wear the full hotel
  // page: hero image + theme, with the message in a card - not a bare screen.
  const themed = props.branding ?? null;
  useBranding(themed ? ({ branding: themed } as GuestContent) : null);
  if (themed) {
    return (
      <div className="app">
        <Hero
          content={{ branding: themed } as GuestContent}
          roomLabel={null}
          L={(v) => (typeof v === "string" ? v : "")}
        />
        <main className="stack">
          <section className="card card--welcome">
            <h2 className="card__title">{title}</h2>
            <p className="card__body">{body}</p>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="state">
      <div className="state__card">
        <h1 className="state__title">{title}</h1>
        <p className="state__body">{body}</p>
      </div>
    </div>
  );
}
